import React from 'react';
import { ScanFaceIcon, UploadIcon, FileCheckIcon, ShieldCheckIcon, CheckCircle2Icon, ClockIcon } from 'lucide-react';
import { Card, StepHeading, Helper, StatusPill } from '../SettingsUI';
import { ShopConfig, VerificationStatus } from '../../../data/shopConfig';
interface Props {
  config: ShopConfig;
  update: (patch: Partial<ShopConfig>) => void;
}
function StatusCard({
  title,
  desc,
  status,
  children
}: {
  title: string;
  desc: string;
  status: VerificationStatus;
  children?: React.ReactNode;
}) {
  const done = status === 'verified' || status === 'pending';
  return <div className={`p-5 rounded-2xl border ${done ? 'border-success/25 bg-success/[0.04]' : 'border-slate-200 bg-white'}`} data-id="element-4233" data-mp="3tng1mcafeybt">
      <div className="flex items-start justify-between gap-3 mb-1" data-id="element-4234" data-mp="3ood89p3t6gjo">
        <div className="flex items-center gap-2.5 min-w-0" data-id="element-4235" data-mp="pz4munpxzrgs">
          <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${done ? 'bg-success/15' : 'bg-slate-100'}`} data-id="element-4236" data-mp="q0pk4l3n5oic">
            {status === 'verified' ? <CheckCircle2Icon className="w-4 h-4 text-success" data-id="element-4237" data-mp="7pbc1kjydgsn" /> : status === 'pending' ? <ClockIcon className="w-4 h-4 text-warning" data-id="element-4238" data-mp="3q3k958foqj4y" /> : <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-4239" data-mp="3q1hluw5x343x" />}
          </span>
          <p className="text-sm font-bold text-dark-navy truncate" data-id="element-4240" data-mp="3u2t5ml240v47">{title}</p>
        </div>
        <StatusPill status={status} data-id="element-4241" data-mp="3g77heg3pafcu" />
      </div>
      <p className="text-xs text-slate-500 leading-relaxed ml-[38px]" data-id="element-4242" data-mp="3l4hfek55nzca">{desc}</p>
      {children && <div className="mt-4 ml-[38px]" data-id="element-4243" data-mp="374y63o0lyhye">{children}</div>}
    </div>;
}
export function Verification({
  config,
  update
}: Props) {
  const isBusiness = config.sellerType === 'business';
  const v = config.verification;
  const setStatus = (patch: Partial<typeof v>) => update({
    verification: {
      ...v,
      ...patch
    }
  });
  const allDone = v.identity === 'verified' && (!isBusiness || v.license !== 'todo');
  return <div className="space-y-5" data-id="element-4244" data-mp="2icwq99ljvhqw">
      <StepHeading title="Verify your business" desc="Verification helps customers know they are dealing with a real business. Only you can see the documents you upload." data-id="element-4245" data-mp="2dcihtqg1e5sc" />

      {allDone && <div className="flex items-start gap-3 p-5 rounded-2xl bg-primary-indigo/[0.04] border border-primary-indigo/15" data-id="element-4246" data-mp="24cdk680nalrd">
          <ShieldCheckIcon className="w-5 h-5 text-primary-indigo flex-shrink-0 mt-0.5" data-id="element-4247" data-mp="3111s2bincsn2" />
          <div data-id="element-4248" data-mp="2x1kip703nbcm">
            <p className="text-sm font-bold text-dark-navy" data-id="element-4249" data-mp="2x1szh9zcf0t3">
              Verified Merchant
            </p>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed" data-id="element-4250" data-mp="2s8847pp4wkxl">
              This badge will appear on your Talab shop, next to your business
              name.
            </p>
          </div>
        </div>}

      <div className="space-y-3" data-id="element-4251" data-mp="3krx0d9l5cmge">
        <StatusCard title="Identity" desc="Confirm who you are with an Emirates ID or passport." status={v.identity} data-id="element-4252" data-mp="3fyu6f0cgjazo">
          {v.identity === 'todo' ? <div className="flex flex-wrap gap-2" data-id="element-4253" data-mp="3s939zr2p7dnj">
              <button onClick={() => setStatus({
            identity: 'verified'
          })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4254" data-mp="3s9v38v8tozan">
                <ScanFaceIcon className="w-4 h-4" data-id="element-4255" data-mp="a1vynhrd1qjo" />
                Scan ID
              </button>
              <button onClick={() => setStatus({
            identity: 'verified'
          })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 hover:border-primary-indigo/50 transition-colors duration-150 ease-out" data-id="element-4256" data-mp="f18hlrf42dgz">
                <UploadIcon className="w-4 h-4" data-id="element-4257" data-mp="xtb59mq66u0q" />
                Upload ID
              </button>
            </div> : <button onClick={() => setStatus({
          identity: 'todo'
        })} className="text-xs font-semibold text-primary-indigo hover:underline" data-id="element-4258" data-mp="k5slcqd0zgnt">
              Replace document
            </button>}
        </StatusCard>

        {isBusiness && <StatusCard title="Trade License" desc="A valid UAE trade licence. We read your business name and expiry from it automatically." status={v.license} data-id="element-4259" data-mp="oxanhhqsbnad">
            {v.license === 'todo' ? <button onClick={() => setStatus({
          license: 'pending',
          licenseNumber: 'CN-1094552',
          licenseExpiry: '14 Mar 2027'
        })} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-indigo text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-colors duration-150 ease-out" data-id="element-4260" data-mp="3tdvamstzn2we">
                <FileCheckIcon className="w-4 h-4" data-id="element-4261" data-mp="2zj35klfeamev" />
                Upload Trade License
              </button> : <div className="flex flex-wrap items-center gap-3" data-id="element-4262" data-mp="34guvxb0e60gy">
                <span className="text-xs text-slate-500" data-id="element-4263" data-mp="2qkho6tlntdfo">
                  Licence {v.licenseNumber} · expires {v.licenseExpiry}
                </span>
                <button onClick={() => setStatus({
            license: 'todo'
          })} className="text-xs font-semibold text-primary-indigo hover:underline" data-id="element-4264" data-mp="3j7tya4amvss3">
                  Replace
                </button>
              </div>}
          </StatusCard>}

        <StatusCard title="Business Review" desc="Our team takes a quick look once your documents are in. Nothing for you to do here." status={v.review} data-id="element-4265" data-mp="3egveuvucmekm" />
      </div>

      <Helper data-id="element-4266" data-mp="ask3ziczjruv">
        {isBusiness ? 'A trade licence is required for business accounts. You can keep setting up your shop while we review it.' : 'Personal sellers only need an ID. A trade licence is not required.'}
      </Helper>
    </div>;
}